import { FC, memo } from 'react'
import ReactTextareaAutosize from 'react-textarea-autosize'
import { cn } from '@/lib/utils'

export interface TextareaAutosizeProps {
  value: string
  onValueChange: (value: string) => void
  textareaRef?: React.RefObject<HTMLTextAreaElement>
  className?: string
  placeholder?: string
  minRows?: number
  maxRows?: number
  maxLength?: number
  disabled?: boolean
  spellCheck?: boolean
  onKeyDown?: (
    event: React.KeyboardEvent<HTMLTextAreaElement> | React.KeyboardEvent<Element>
  ) => void
  onPaste?: (event: React.ClipboardEvent) => void
  onCompositionStart?: (event: React.CompositionEvent) => void
  onCompositionEnd?: (event: React.CompositionEvent) => void
  onSelect?: (event: React.SyntheticEvent<HTMLTextAreaElement>) => void
  onMouseUp?: (event: React.MouseEvent<HTMLTextAreaElement>) => void
}

// Memoize the component to prevent unnecessary re-renders
const TextareaAutosize = memo<TextareaAutosizeProps>(
  ({
    value,
    onValueChange,
    textareaRef,
    className,
    placeholder = '',
    minRows = 1,
    maxRows = 6,
    maxLength,
    disabled,
    spellCheck,
    onKeyDown = () => {},
    onPaste = () => {},
    onCompositionStart = () => {},
    onCompositionEnd = () => {},
    onSelect,
    onMouseUp
  }) => {
    return (
      <ReactTextareaAutosize
        ref={textareaRef}
        className={cn(
          'order bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full resize-none rounded-md px-1.5 py-2 text-base leading-normal focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        aria-label="Chat input"
        role="textbox"
        minRows={minRows}
        maxRows={minRows > maxRows ? minRows : maxRows}
        placeholder={placeholder}
        value={value}
        maxLength={maxLength}
        disabled={disabled}
        spellCheck={spellCheck}
        onChange={(event) => onValueChange(event.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onCompositionStart={onCompositionStart}
        onCompositionEnd={onCompositionEnd}
        onSelect={onSelect}
        onMouseUp={onMouseUp}
      />
    )
  }
)

TextareaAutosize.displayName = 'TextareaAutosize'

export { TextareaAutosize }
